import { AnimatePresence, motion } from "framer-motion";
import React, { useState } from "react";
import { getSkillRelations, skill } from "../../../static/data";
import { SectionSubtitle, SectionTitle } from "../../SectionTitleElements";
import { SkillsSEO } from "../../SEO";
import {
  SkillBox,
  SkillContainer,
  SkillContent,
  SkillData,
  SkillGroup,
  SkillLevel,
  SkillName,
  SkillSection,
  SkillWrapper,
} from "./SkillsElements";
import SkillTooltip from "./SkillTooltip";

const Skills = ({ darkMode }) => {
  const [activeSkill, setActiveSkill] = useState(null);
  const [hoveredSkill, setHoveredSkill] = useState(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.4, ease: "easeOut" },
    },
  };

  // Group skills by level for better organization
  const groupedSkills = {
    Intermediate: skill.filter((s) => s.level === "Intermediate"),
    Beginner: skill.filter((s) => s.level === "Beginner"),
  };

  const handleSkillHover = (skillId) => {
    setHoveredSkill(skillId);

    // Get related projects and certifications
    const relations = getSkillRelations(skillId);

    // Only set as active if there are related items
    if (relations.projects.length > 0 || relations.certifications.length > 0) {
      setActiveSkill(skillId);
    } else {
      setActiveSkill(null);
    }
  };

  const handleSkillLeave = () => {
    setHoveredSkill(null);
    setActiveSkill(null);
  };

  // Function to get the skill item by ID
  const getSkillById = (skillId) => {
    return skill.find((s) => s.id === skillId);
  };

  // Transform project and certification data for the tooltip
  const transformRelationsData = (relations) => {
    const projects = relations.projects.map((project) => ({
      id: project.id,
      title: project.project_title,
      image: project.project_image,
      link: project.project_link,
    }));

    const certifications = relations.certifications.map((cert) => ({
      id: cert.id,
      title: cert.certifications_title,
      image: cert.certifications_image,
      link: cert.certifications_link,
    }));

    return { projects, certifications };
  };

  return (
    <>
      {/* Skills-specific SEO */}
      <SkillsSEO sectionTitle="My Technical Skills & Expertise" />

      <SkillSection id="skills">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
        >
          <SectionSubtitle>Technologies I use</SectionSubtitle>
          <SectionTitle>My Skills</SectionTitle>

          <SkillContainer>
            <SkillContent light={darkMode ? 1 : 0}>
              <SkillBox>
                <SkillGroup>
                  {/* First render Intermediate skills */}
                  {groupedSkills.Intermediate.map((skillItem) => {
                    const relations = getSkillRelations(skillItem.id);
                    const hasRelations =
                      relations.projects.length > 0 ||
                      relations.certifications.length > 0;
                    const transformedData = transformRelationsData(relations);

                    return (
                      <motion.div key={skillItem.id} variants={itemVariants}>
                        <SkillWrapper
                          onMouseEnter={() => handleSkillHover(skillItem.id)}
                          onMouseLeave={handleSkillLeave}
                          onClick={() => handleSkillHover(skillItem.id)}
                          style={{
                            position: "relative",
                            zIndex: activeSkill === skillItem.id ? 99999 : 1,
                          }}
                        >
                          <SkillData
                            light={darkMode ? 1 : 0}
                            style={{
                              cursor: hasRelations ? "pointer" : "default",
                              borderColor:
                                hoveredSkill === skillItem.id
                                  ? "rgba(220, 38, 38, 0.6)"
                                  : "transparent",
                              position: "relative",
                              zIndex: 2,
                            }}
                          >
                            <i
                              className="bx bxs-badge-check"
                              aria-hidden="true"
                            ></i>
                            <div>
                              <SkillName light={darkMode ? 1 : 0}>
                                {skillItem.skill_name}
                              </SkillName>
                              <SkillLevel light={darkMode ? 1 : 0}>
                                {skillItem.level}
                              </SkillLevel>
                            </div>
                          </SkillData>

                          <AnimatePresence>
                            {activeSkill === skillItem.id && (
                              <SkillTooltip
                                skillItem={skillItem}
                                relatedProjects={transformedData.projects}
                                relatedCertifications={
                                  transformedData.certifications
                                }
                                darkMode={darkMode}
                              />
                            )}
                          </AnimatePresence>
                        </SkillWrapper>
                      </motion.div>
                    );
                  })}

                  {/* Then render Beginner skills */}
                  {groupedSkills.Beginner.map((skillItem) => {
                    const relations = getSkillRelations(skillItem.id);
                    const hasRelations =
                      relations.projects.length > 0 ||
                      relations.certifications.length > 0;
                    const transformedData = transformRelationsData(relations);

                    return (
                      <motion.div key={skillItem.id} variants={itemVariants}>
                        <SkillWrapper
                          onMouseEnter={() => handleSkillHover(skillItem.id)}
                          onMouseLeave={handleSkillLeave}
                          onClick={() => handleSkillHover(skillItem.id)}
                          style={{
                            position: "relative",
                            zIndex: activeSkill === skillItem.id ? 99999 : 1,
                          }}
                        >
                          <SkillData
                            light={darkMode ? 1 : 0}
                            style={{
                              cursor: hasRelations ? "pointer" : "default",
                              borderColor:
                                hoveredSkill === skillItem.id
                                  ? "rgba(220, 38, 38, 0.6)"
                                  : "transparent",
                              position: "relative",
                              zIndex: 2,
                            }}
                          >
                            <i
                              className="bx bxs-badge-check"
                              aria-hidden="true"
                            ></i>
                            <div>
                              <SkillName light={darkMode ? 1 : 0}>
                                {skillItem.skill_name}
                              </SkillName>
                              <SkillLevel light={darkMode ? 1 : 0}>
                                {skillItem.level}
                              </SkillLevel>
                            </div>
                           
                          </SkillData>

                          <AnimatePresence>
                            {activeSkill === skillItem.id && (
                              <SkillTooltip
                                skillItem={skillItem}
                                relatedProjects={transformedData.projects}
                                relatedCertifications={
                                  transformedData.certifications
                                }
                                darkMode={darkMode}
                              />
                            )}
                          </AnimatePresence>
                        </SkillWrapper>
                      </motion.div>
                    );
                  })}
                </SkillGroup>
              </SkillBox>
            </SkillContent>
          </SkillContainer>
        </motion.div>
      </SkillSection>
    </>
  );
};

export default Skills;
